class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    const trap = () => {
      if (!this.classList.contains('active')) return;
      const containerToTrapFocusOn = this.classList.contains('is-empty')
        ? this.querySelector('.drawer__inner-empty')
        : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    };

    // Already open (the loading state opened it): contents were just swapped,
    // so no transition will run; trap focus on the new markup now.
    if (this.classList.contains('active')) {
      trap();
    } else {
      // here the animation doesn't seem to always get triggered. A timeout seem to help
      setTimeout(() => {
        this.classList.add('animate', 'active');
      });
      this.addEventListener('transitionend', trap, { once: true });
    }

    document.body.classList.add('overflow-hidden');
  }

  // Optimistic open on add to cart: the drawer slides in at once with a quiet
  // placeholder line while /cart/add runs; renderContents swaps the real markup in.
  showLoading(triggeredBy) {
    if (!this.classList.contains('is-loading')) {
      this.wasEmpty = this.classList.contains('is-empty');
      this.classList.remove('is-empty');
      this.classList.add('is-loading');
      this.querySelector('.drawer__inner')?.setAttribute('aria-busy', 'true');
      this.querySelector('.drawer__contents')?.insertAdjacentHTML(
        'afterbegin',
        '<div class="cart-drawer__placeholder" aria-hidden="true"><span></span><span></span><span></span></div>'
      );
      // the placeholder sits at the top of the list; scroll it into view so a long cart
      // does not look settled while the add runs (either box scrolls, see the CSS)
      ['cart-drawer-items', '.drawer__inner'].forEach((selector) => {
        const scroller = this.querySelector(selector);
        if (scroller) scroller.scrollTop = 0;
      });
    }
    this.open(triggeredBy);
  }

  // The add failed: drop the placeholder and close, so the error shows at the form.
  hideLoading() {
    if (!this.classList.contains('is-loading')) return;
    this.clearLoading();
    if (this.wasEmpty) this.classList.add('is-empty');
    this.close();
  }

  clearLoading() {
    this.classList.remove('is-loading');
    this.querySelector('.drawer__inner')?.removeAttribute('aria-busy');
    this.querySelector('.cart-drawer__placeholder')?.remove();
  }

  close() {
    this.classList.remove('active');
    // Focus returns to what opened the drawer; with no live opener (Safari does not focus a
    // clicked button, or the opener was re-rendered) it goes to the bag icon, never body.
    const opener =
      this.activeElement?.isConnected && this.activeElement !== document.body
        ? this.activeElement
        : document.getElementById('cart-icon-bubble');
    removeTrapFocus(opener);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.clearLoading();
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
